import { useQuery, useQueryClient } from '@tanstack/react-query';
import { odstep, stanWToku } from '@/lib/odswiezanie';
import type { ColumnDef } from '@tanstack/react-table';
import { Ban, Check, FileCog, Loader2, RotateCcw, ShieldAlert, Undo2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { DataTable, type DataTableState } from '@/components/DataTable';
import { IKONA_STANU, StatusZmiany } from '@/components/StatusZmiany';
import { CofnijOperacje } from '@/components/CofnijOperacje';
import { useAuth } from '@/lib/auth';
import { opisPrzed, type OpisPrzed } from '@/lib/stan-przed';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { Select } from '@/components/ui/select';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';
import { useToast } from '@/components/ui/toast';
import {
  autorZZewnatrz,
  CHANGE_STATUSES,
  fetchChanges,
  fetchOperation,
  fetchSummary,
  ponowOperacje,
  STEPPER_PATH,
  waitingDays,
  zamknijNieudane,
  zwinOczekujace,
  zamknijOperacje,
  type ChangeStatus,
  type Operation,
} from '@/lib/changes-api';
import { cn } from '@/lib/utils';

/**
 * Monitor zmian konfiguracji — kolejka config-down do wglądu.
 *
 * ⚠️ STAN NA 2026-08-05: ETAPY SĄ JUŻ PRAWDZIWE. Wcześniej stało tu, że nic nie ustawia statusu
 * innego niż `QUEUED`, bo agent nie istnieje — to przestało być prawdą wraz z kanałem w dół.
 * Agent pobiera operacje (`SENT`), wykonuje je w jednej transakcji na bazie SPS i melduje
 * `CONFIRMED` albo `FAILED` z powodem, który pokazujemy niżej.
 *
 * ⚠️ `FAILED` NIE ZNACZY „AWARIA" — najczęstszy powód to odmowa wartownika, czyli ktoś zmienił
 * ten sam wiersz w SPSUI po ostatnim odczycie agenta. Dlatego powód musi być widoczny na ekranie,
 * a nie tylko w logu: bez niego nie da się rozstrzygnąć, czy poprawić dane, czy pogadać
 * z serwisantem.
 *
 * `APPLIED` pozostaje nieosiągalne i to jest świadome: agent zapisuje i potwierdza widoczność
 * w jednym przejściu, a niezgodność odczytu po zapisie wycofuje całą operację.
 *
 * Wiek najstarszej zmiany w kolejce zostaje głównym sygnałem zdrowia — kolejka, której nikt nie
 * opróżnia, rośnie, i to jedyna miara niezależna od tego, co melduje agent.
 */

/** Cztery etapy toru głównego. `FAILED`/`SUPERSEDED` to zakończenia poza torem, nie kolejne kroki. */
/**
 * NA KTÓRYCH WĘZŁACH OPERACJA JUŻ JEST — lista wydań (`config_delivery`).
 *
 * ⚠️ PUSTO ZNACZY „TU SIĘ TEGO NIE PROWADZI", a nie „nikt tego nie wykonał". W trybie `ZWYKLY`
 * wyspa ma jedną fizyczną bazę i wydań per węzeł nie ma po co zapisywać — dlatego brak listy
 * nie może wyglądać na awarię. Mówimy więc wprost: nic nie rysujemy.
 */
function WydaniaWezlow({ wydania }: { wydania: Operation['wydania'] }) {
  const { t } = useI18n();
  if (wydania === undefined || wydania.length === 0) return null;

  const potwierdzonych = wydania.filter((w) => w.status === 'CONFIRMED').length;

  return (
    <div className="rounded-md border border-border p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-xs font-semibold">{t('chg.wydaniaTytul')}</span>
        <span className="text-[11px] tabular-nums text-muted-foreground">
          {t('chg.wydaniaLicznik').replace('{ile}', String(potwierdzonych)).replace('{wszystkich}', String(wydania.length))}
        </span>
      </div>
      <ul className="mt-2 space-y-1">
        {wydania.map((w) => (
          <li key={w.nodeId} className="flex flex-wrap items-baseline gap-2 text-[11px]">
            <span className="font-mono">{w.nodeId}</span>
            <StatusZmiany status={w.status} />
            {w.status === 'CONFIRMED' && w.confirmedAt !== null && (
              <span className="tabular-nums text-muted-foreground">
                {new Date(w.confirmedAt).toLocaleString('pl-PL')}
              </span>
            )}
            {/*
              ⚠️⚠️ ILE TEN WĘZEŁ REALNIE ZAPISAŁ, a ile pominął — i bez tej liczby „potwierdzona"
              potrafi znaczyć DWIE RÓŻNE RZECZY. Przy wyrównaniu geometrii większość węzłów
              pomija wszystko (bo te wiersze już ma), a jeden zakłada 468 brakujących. Ekran
              pokazywał wtedy to samo zielone „potwierdzona" w obu przypadkach, więc po kliknięciu
              nie dawało się odróżnić „zadziałało" od „nie było czego robić". Liczby jechały
              w odpowiedzi od początku (`applied`, `skipped`) i nikt ich nie rysował.

              ⚠️ POKAZUJEMY, GDY CHOĆ JEDNA JEST NIEZEROWA: starsza instancja oddałaby zera,
              a „zap. 0 · pom. 0" przy każdym węźle to szum, nie informacja.
            */}
            {w.applied + w.skipped > 0 && (
              <span className="tabular-nums text-muted-foreground">
                {t('chg.wydanieLiczby')
                  .replace('{zapisanych}', String(w.applied))
                  .replace('{pominietych}', String(w.skipped))}
              </span>
            )}
            {/* Powód od TEGO węzła — przy kilku bazach „nieudana" bez wskazania węzła nie mówi nic. */}
            {w.error !== null && <span className="text-[var(--destructive)]">{w.error}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Stepper({ status }: { status: ChangeStatus }) {
  const { t } = useI18n();
  const offTrack = status === 'FAILED' || status === 'SUPERSEDED';
  const reachedIdx = offTrack ? 0 : STEPPER_PATH.indexOf(status as (typeof STEPPER_PATH)[number]);

  /**
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   *  ⚠️⚠️ KOLOR ETAPU (zgłoszone 01.09.2026): „daj jakieś kolory statusu, jak jest wszystko git, to
   *  niech te kafelki są na zielono. Żółty jak dany etap się wykonuje, czerwony jak się spierdolił.
   *  Ludzie od razu myślą, że coś jest źle, jak nie świeci się na zielono".
   *
   *  Do tej pory KAŻDY osiągnięty etap był żółty — także w operacji potwierdzonej na wszystkich
   *  węzłach. Czyli kolor nie odróżniał sukcesu od kłopotu i uczył czytać go jako ostrzeżenie.
   *
   *  ⚠️ ETAP BIEŻĄCY JEST ŻÓŁTY, ale tylko dopóki tor się nie skończył: `CONFIRMED` to koniec, więc
   *  wtedy wszystkie cztery są zielone. Żółty na ostatnim etapie znaczyłby „coś jeszcze trwa".
   *
   *  ⚠️ PRZY `FAILED` NIE MALUJEMY ETAPU NA CZERWONO, i to jest świadome: portal wie, ŻE się nie
   *  udało, ale nie wie, NA KTÓRYM etapie — a wydania per węzeł (niżej) wiedzą i tam stoi powód.
   *  Czerwona jest cała ramka toru: sygnał jest, a żaden etap nie dostaje winy, której nie da się
   *  udowodnić. Zakolejkowanie ZOSTAJE zielone, bo ono się udało.
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   */
  const stanEtapu = (i: number): 'ZROBIONE' | 'W_TOKU' | 'PRZED' => {
    if (offTrack) return i === 0 ? 'ZROBIONE' : 'PRZED';
    if (i < reachedIdx) return 'ZROBIONE';
    if (i > reachedIdx) return 'PRZED';
    return status === 'CONFIRMED' ? 'ZROBIONE' : 'W_TOKU';
  };

  return (
    <div>
      <ol
        className={cn(
          'flex flex-wrap items-stretch gap-2',
          offTrack && 'rounded-md border border-destructive/40 bg-destructive/5 p-2',
        )}
      >
        {STEPPER_PATH.map((stage, i) => {
          const stan = stanEtapu(i);
          const Icon = IKONA_STANU[stage];
          return (
            <li
              key={stage}
              className={cn(
                'flex min-w-32 flex-1 flex-col gap-1 rounded-md border p-2 text-xs',
                stan === 'ZROBIONE'
                  ? 'border-[var(--success)]/40 bg-[var(--success)]/10'
                  : stan === 'W_TOKU'
                    ? 'border-[var(--warning)]/40 bg-[var(--warning)]/10'
                    : 'border-dashed border-border text-muted-foreground',
              )}
            >
              <span className="flex items-center gap-1.5 font-medium">
                <Icon className="size-3" />
                {t(`chg.status.${stage}`)}
              </span>
              <span>
                {stan === 'ZROBIONE'
                  ? t('chg.stageReached')
                  : stan === 'W_TOKU'
                    ? t('chg.stageInProgress')
                    : t('chg.stageUnreachable')}
              </span>
            </li>
          );
        })}
      </ol>
      {offTrack && (
        <p className="mt-2">
          <StatusZmiany status={status} />
        </p>
      )}
      {/*
        Nota jest WARUNKOWA. Bezwarunkowo twierdziła „operacja zostaje w pierwszym etapie", co przy
        operacji już potwierdzonej przeczyło temu, co pokazuje stepper obok. Etykieta nie może
        obiecywać czegoś, czego nie mierzy — to ta sama pomyłka co licznik „netto +40 os." w Limitach.
      */}
      <p className="mt-2 text-xs text-muted-foreground">
        {status === 'QUEUED' ? t('chg.stepperNote') : t('chg.stepperNoteAdvanced')}
      </p>
    </div>
  );
}

/**
 * CZYNNOŚCI NA NIEUDANEJ OPERACJI — zamknięcie i ponowienie.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ ZGŁOSZENIE Z PRODUKCJI PESA (04.09.2026): „mam »wymaga uwagi« ale nie wiem co to za
 *  operacje nieudane ani jak je potwierdzić/anulować czy skasować błąd czy cokolwiek". Do tego
 *  dnia ten ekran POKAZYWAŁ porażki i nie dawał z nimi zrobić NIC — a stan wyspy liczył je bez
 *  końca, więc żółte „wymaga uwagi" było nie do zgaszenia.
 *
 *  ⚠️ DWA PRZYCISKI ZNACZĄ DWIE RÓŻNE RZECZY I NIE WOLNO ICH ZLEWAĆ:
 *   • „Zamknij" = widzieliśmy, nie da się tego dokończyć. Operacja ZOSTAJE nieudana, przestaje
 *     tylko liczyć się do stanu wyspy. Nic nie jedzie na automat.
 *   • „Wyślij ponownie" = wracamy do kolejki i próbujemy TĄ SAMĄ wartością. Może nadpisać to, co
 *     jest teraz na automacie — dlatego serwer odmawia, gdy po tej porażce te same klucze zmieniła
 *     inna operacja, i mówi wprost, którą.
 *
 *  ⚠️ POWÓD PORAŻKI STOI PRZY PRZYCISKACH, nie w innej sekcji. Najczęstszym powodem jest odmowa
 *  wartownika (ktoś zmienił tę wartość na maszynie ręcznie) — a to zmienia decyzję z „ponów" na
 *  „zamknij". Przycisk bez powodu obok jest zaproszeniem do zgadywania.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
function CzynnosciNieudanej({
  op,
  groupId,
  onZmiana,
}: {
  /**
   * ⚠️ TYLKO POLA, KTÓRYCH TEN KOMPONENT UŻYWA — nie cały `Operation`. Lista i szczegóły mają
   * RÓŻNE kształty (szczegóły dokładają `tables`, lista nie), więc wymuszenie pełnego typu
   * przywiązałoby przyciski do jednego z tych dwóch widoków bez żadnego powodu.
   */
  op: {
    id: string;
    status: ChangeStatus;
    error: string | null;
    zamknietaO?: string | null;
    zamknietaPrzez?: string | null;
    ponowien?: number;
  };
  groupId: string;
  onZmiana: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [trwa, setTrwa] = useState<'' | 'zamknij' | 'ponow'>('');
  if (op.status !== 'FAILED') return null;

  const zamknieta = op.zamknietaO !== null && op.zamknietaO !== undefined;

  const zrob = async (co: 'zamknij' | 'ponow') => {
    setTrwa(co);
    try {
      if (co === 'zamknij') {
        await zamknijOperacje(op.id, groupId);
        toast.success(t('chg.zamknieta'));
      } else {
        const w = await ponowOperacje(op.id, groupId);
        toast.success(t('chg.ponowiona').replace('{ile}', String(w.usunieteWydania)));
      }
      onZmiana();
    } catch (e) {
      /**
       * ⚠️ TREŚĆ ODMOWY SERWERA POKAZUJEMY DOSŁOWNIE. Serwer mówi, KTÓRA późniejsza operacja
       * zmieniła te same wartości i co z tym zrobić — zastąpienie tego własnym „nie udało się"
       * zabrałoby jedyną użyteczną część odpowiedzi.
       */
      toast.error(e instanceof Error ? e.message : t('common.error'));
    } finally {
      setTrwa('');
    }
  };

  return (
    <div className="rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/5 p-3">
      <p className="text-xs font-medium">{t('chg.nieudanaTytul')}</p>
      {op.error !== null && op.error !== '' && (
        <p className="mt-1 whitespace-pre-wrap break-words text-[11px] text-muted-foreground">{op.error}</p>
      )}
      {zamknieta ? (
        <p className="mt-2 text-[11px] text-muted-foreground">
          {t('chg.zamknietaPrzez')
            .replace('{kto}', op.zamknietaPrzez ?? '—')
            .replace('{kiedy}', new Date(op.zamknietaO!).toLocaleString('pl-PL'))}
        </p>
      ) : null}
      {(op.ponowien ?? 0) > 0 && (
        <p className="mt-1 text-[11px] text-muted-foreground">
          {t('chg.ponawiana').replace('{ile}', String(op.ponowien))}
        </p>
      )}
      <div className="mt-2 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" disabled={trwa !== '' || zamknieta} onClick={() => void zrob('zamknij')}>
          {trwa === 'zamknij' ? <Loader2 className="size-3.5 animate-spin" /> : <Check className="size-3.5" />}
          {zamknieta ? t('chg.jużZamknięta') : t('chg.zamknij')}
        </Button>
        <Button size="sm" variant="outline" disabled={trwa !== ''} onClick={() => void zrob('ponow')}>
          {trwa === 'ponow' ? <Loader2 className="size-3.5 animate-spin" /> : <RotateCcw className="size-3.5" />}
          {t('chg.ponow')}
        </Button>
      </div>
      <p className="mt-2 text-[11px] leading-snug text-muted-foreground">{t('chg.nieudanaPodpowiedz')}</p>
    </div>
  );
}

/** Stan sprzed zmiany w komórce tabeli — patrz `lib/stan-przed.ts`. */
function KomorkaPrzed({ opis }: { opis: OpisPrzed }) {
  const { t } = useI18n();
  if (opis.rodzaj === 'brak') return <span className="text-muted-foreground" title={t('chg.przedBrakHint')}>—</span>;
  if (opis.rodzaj === 'nieBylo') return <span className="text-muted-foreground">{t('chg.przedNieBylo')}</span>;
  if (opis.rodzaj === 'rozny') {
    return (
      <Badge variant="warning" title={opis.wezly.join(', ')}>
        {t('chg.przedRozny')}
      </Badge>
    );
  }
  return <span className="font-mono text-muted-foreground">{JSON.stringify(opis.wartosci)}</span>;
}

function DetailModal({ opId, groupId, onClose }: { opId: string; groupId: string; onClose: () => void }) {
  const { t } = useI18n();
  const klient = useQueryClient();
  const { isAdmin } = useAuth();
  const { data, isPending } = useQuery({
    queryKey: ['changes', 'detail', opId, groupId],
    queryFn: () => fetchOperation(opId, groupId),
  });
  /** Okno cofnięcia — tylko administrator, jak cały edytor bazy (`AdminGuard`). */
  const [cofanie, setCofanie] = useState(false);
  /**
   * PRZYCISK TYLKO WTEDY, GDY JEST Z CZEGO COFAĆ: operacja wykonana, nikt jej jeszcze nie cofa, a choć
   * jedna zmiana ma stan sprzed. Resztę powodów odmowy mówi okno — i mówi je konkretnie.
   */
  const moznaCofac =
    isAdmin && data !== undefined && data.status === 'CONFIRMED' && !data.cofnietaPrzez && data.changes.some((c) => c.przed.length > 0);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        title={t('chg.detailTitle')}
        description={data ? data.title : t('common.loading')}
        className="w-[min(60rem,calc(100vw-2rem))]"
      >
        {isPending || !data ? (
          <p className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t('common.loading')}
          </p>
        ) : (
          <div className="space-y-4">
            <Stepper status={data.status} />

            {/*
              ⚠️ CZYNNOŚCI ZARAZ POD STEPPEREM, PRZED TABELKĄ WYDAŃ. Człowiek, który tu wchodzi
              z pulpitu, przyszedł zrobić jedną rzecz — a nie przewijać do końca długiego okna.
            */}
            <CzynnosciNieudanej
              op={data}
              groupId={groupId}
              onZmiana={() => {
                void klient.invalidateQueries({ queryKey: ['changes'] });
                void klient.invalidateQueries({ queryKey: ['overview'] });
              }}
            />

            {/*
              ⚠️⚠️ NA KTÓRE BAZY ZMIANA JUŻ WESZŁA — pytanie, na które ekran do 25.08.2026 nie
              umiał odpowiedzieć, bo portal tego nie zapisywał. W Smart Syncu wyspa ma kilka
              WŁASNYCH baz i „potwierdzona" znaczy „wszystkie", więc bez tej listy stan „w toku"
              nie mówi, na co się czeka: na master, na automat odcięty od sieci, czy na wszystko.
            */}
            <WydaniaWezlow wydania={data.wydania} />

            {(data.cofaOpId || data.cofnietaPrzez || moznaCofac) && (
              <div className="flex flex-wrap items-center gap-2 text-xs">
                {data.cofaOpId && (
                  <Badge variant="neutral">
                    {t('chg.cofa')} <span className="ml-1 font-mono">{data.cofaOpId.slice(0, 8)}</span>
                  </Badge>
                )}
                {data.cofnietaPrzez && (
                  <Badge variant="warning" title={data.cofnietaPrzez.title}>
                    {t('chg.cofnietaPrzez').replace('{status}', t(`chg.status.${data.cofnietaPrzez.status}`))}
                  </Badge>
                )}
                {moznaCofac && (
                  <Button type="button" size="sm" variant="outline" className="ml-auto" onClick={() => setCofanie(true)}>
                    <Undo2 />
                    {t('chg.cofnij')}
                  </Button>
                )}
              </div>
            )}

            <dl className="grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
              <div className="flex gap-2">
                <dt className="text-muted-foreground">{t('chg.opId')}</dt>
                <dd className="font-mono">{data.id}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-muted-foreground">{t('chg.created')}</dt>
                <dd className="tabular-nums">{new Date(data.createdAt).toLocaleString('pl-PL')}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-muted-foreground">{t('chg.author')}</dt>
                {/*
                  ⚠️ KOLEJNOŚĆ: konto portalu, potem sprawca z zewnątrz, a myślnik dopiero na
                  końcu. Operacja z `/api/v1` NIE MA konta w `createdBy` i przed dołożeniem
                  `externalActor` wyglądała tu na operację bez autora — czyli dokładnie tak,
                  jakby portal zgubił informację, której nigdy nie miał.
                */}
                <dd>
                  {data.createdBy?.email ?? data.externalActor ?? '—'}
                  {autorZZewnatrz(data) !== null && (
                    <Badge variant="neutral" className="ml-2" title={t('chg.externalHint')}>
                      {t('chg.external')}
                    </Badge>
                  )}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-muted-foreground">{t('chg.source')}</dt>
                <dd>{t(`chg.source.${data.source}`)}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-muted-foreground">{t('chg.waiting')}</dt>
                <dd className="tabular-nums">
                  {t('chg.waitingDays').replace('{n}', String(waitingDays(data.createdAt)))}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-muted-foreground">{t('chg.confirmedAt')}</dt>
                <dd>{data.confirmedAt ? new Date(data.confirmedAt).toLocaleString('pl-PL') : t('chg.never')}</dd>
              </div>
            </dl>

            {data.authorNote && (
              <p className="rounded-md border border-border bg-muted/40 p-3 text-xs">
                <span className="text-muted-foreground">{t('chg.authorNote')}: </span>
                {data.authorNote}
              </p>
            )}
            {data.error && (
              <p className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
                {data.error}
              </p>
            )}
            {/*
              ⚠️⚠️ ZASTRZEŻENIE DO UDANEJ OPERACJI, WIĘC NIE NA CZERWONO. Agent wolno pominąć
              kolumnę-wypełniacz, której baza klienta nie ma (awaria Rosti 10.09.2026) — operacja
              się UDAŁA, tylko wiersz powstał bez tej kolumny. Malowanie tego jak błędu kazałoby
              operatorowi powtarzać zrobioną robotę, a schowanie tego znaczyłoby, że wiersz różni
              się od zleconego i nikt o tym nie wie.
            */}
            {data.uwagi && (
              <p className="rounded-md border border-[var(--warning)]/40 bg-muted/40 p-3 text-xs text-[var(--warning)]">
                {data.uwagi}
              </p>
            )}

            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-xs">
                <thead className="bg-card">
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="px-3 py-2 font-medium">{t('chg.seq')}</th>
                    <th className="px-3 py-2 font-medium">{t('chg.table')}</th>
                    <th className="px-3 py-2 font-medium">{t('chg.action')}</th>
                    <th className="px-3 py-2 font-medium">{t('chg.key')}</th>
                    <th className="px-3 py-2 font-medium">{t('chg.przed')}</th>
                    <th className="px-3 py-2 font-medium">{t('chg.data')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.changes.map((c) => (
                    <tr key={c.seq} className="border-b border-border/60 last:border-b-0 align-top">
                      <td className="px-3 py-2 font-mono tabular-nums">{c.seq}</td>
                      <td className="px-3 py-2 font-mono">{c.tableName}</td>
                      <td className="px-3 py-2">
                        <span className="font-mono font-medium">{c.action}</span>{' '}
                        <span className="text-muted-foreground">{t(`chg.actionName.${c.action}`)}</span>
                      </td>
                      <td className="px-3 py-2 font-mono">{JSON.stringify(c.pk)}</td>
                      <td className="px-3 py-2">
                        <KomorkaPrzed opis={opisPrzed(c.action, (c.payload ?? null) as Record<string, unknown> | null, c.przed)} />
                      </td>
                      <td className="px-3 py-2 font-mono text-muted-foreground">
                        {c.payload ? JSON.stringify(c.payload) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
      {cofanie && (
        <CofnijOperacje
          opId={opId}
          groupId={groupId}
          onClose={() => setCofanie(false)}
          onZakolejkowano={() => {
            void klient.invalidateQueries({ queryKey: ['changes'] });
            void klient.invalidateQueries({ queryKey: ['overview'] });
          }}
        />
      )}
    </Dialog>
  );
}

export function Changes() {
  const { t } = useI18n();
  const { groupId, island, islands, setGroupId } = useIslands();

  const [state, setState] = useState<DataTableState>({ search: '', page: 1, pageSize: 25, sort: 'createdAt', dir: 'desc' });
  /**
   * ⚠️⚠️ FILTR STATUSU CZYTANY Z ADRESU — to on domyka drogę z pulpitu. Zgłoszenie z produkcji
   * PESA brzmiało „nie wiem gdzie to sprawdzić": pulpit mówił o 37 nieudanych operacjach, a ten
   * ekran wymagał wyklikania filtru, o którym nic nie mówiło. Bez tego jednego `useSearchParams`
   * odnośnik z pulpitu wpadałby na pełną listę i człowiek dalej by szukał.
   */
  const [szukajka] = useSearchParams();
  const zAdresu = szukajka.get('status');
  /**
   * ⚠️⚠️ WYSPA Z ADRESU PRZESTAWIA GLOBALNY WYBÓR. Ten ekran bierze `groupId` z kontekstu
   * (jeden wybór na cały portal), więc bez tego odnośnik z pulpitu — wystawiony w wierszu
   * KONKRETNEJ wyspy — otwierałby zmiany tej, która jest wybrana. Na instancji z jedną wyspą
   * nikt by tego nie zauważył; na wielu prowadziłby po cichu w złe miejsce.
   *
   * ⚠️ TYLKO GDY RÓŻNA I ZNANA: przestawianie na każdym przebiegu dawałoby pętlę, a wyspa
   * z adresu, której to konto nie widzi, nie może wyprzeć poprawnego wyboru.
   */
  const wyspaZAdresu = szukajka.get('groupId');
  useEffect(() => {
    if (wyspaZAdresu === null || wyspaZAdresu === groupId) return;
    if (!islands.some((w) => w.id === wyspaZAdresu)) return;
    setGroupId(wyspaZAdresu);
  }, [wyspaZAdresu, groupId, islands, setGroupId]);
  const [status, setStatus] = useState<'' | ChangeStatus>(
    zAdresu !== null && (CHANGE_STATUSES as readonly string[]).includes(zAdresu) ? (zAdresu as ChangeStatus) : '',
  );
  const [source, setSource] = useState<'' | 'MODULE' | 'AUTHORING'>('');
  const [tableName, setTableName] = useState('');
  const [openOp, setOpenOp] = useState<string | null>(null);
  const [zamykanie, setZamykanie] = useState(false);
  /** Tabela wybrana do zwinięcia i okno potwierdzenia — uzasadnienie przy samym bloku niżej. */
  const [doZwiniecia, setDoZwiniecia] = useState('');
  const [zwijanie, setZwijanie] = useState(false);
  const [pytamOZwiniecie, setPytamOZwiniecie] = useState(false);
  const toast = useToast();
  const klient = useQueryClient();

  const summary = useQuery({
    queryKey: ['changes', 'summary', groupId],
    /** Podsumowanie liczb nad tabelą musi zmieniać się razem z nią, inaczej przeczą sobie. */
    refetchInterval: (q) =>
      odstep(Object.entries(q.state.data?.statuses ?? {}).some(([s, ile]) => ile > 0 && stanWToku(s))),
    queryFn: () => fetchSummary(groupId!),
    enabled: Boolean(groupId),
  });

  const params = useMemo(
    () => ({
      ...state,
      groupId: groupId ?? '',
      ...(status ? { status } : {}),
      ...(source ? { source } : {}),
      ...(tableName ? { tableName } : {}),
    }),
    [state, groupId, status, source, tableName],
  );

  const { data, isPending, isFetching, refetch } = useQuery({
    queryKey: ['changes', 'list', params],
    queryFn: () => fetchChanges(params),
    enabled: Boolean(groupId),
    /**
     * ⚠️ SZYBKO, DOPÓKI COŚ JEST W TOKU — i to jest cały sens tego ekranu. Stan zmiany przestawia
     * MASZYNA KLIENTA, nie portal, więc bez odpytywania zegarek „w toku" wisiałby do ręcznego
     * odświeżenia (zgłoszone 2026-08-18). Gdy wszystko jest potwierdzone, tempo samo wraca
     * do spokojnego: warunek liczy się z danych, które już mamy.
     */
    refetchInterval: (q) => odstep((q.state.data?.rows ?? []).some((r) => stanWToku(r.status))),
  });

  const onStateChange = useCallback((patch: Partial<DataTableState>) => setState((s) => ({ ...s, ...patch })), []);

  const columns = useMemo<ColumnDef<Operation>[]>(
    () => [
      {
        id: 'createdAt',
        header: t('chg.created'),
        meta: { exportValue: (r) => r.createdAt },
        cell: ({ row }) => (
          <div className="whitespace-nowrap text-xs tabular-nums">
            {new Date(row.original.createdAt).toLocaleString('pl-PL')}
            <div className="text-muted-foreground">
              {t('chg.waitingDays').replace('{n}', String(waitingDays(row.original.createdAt)))}
            </div>
          </div>
        ),
      },
      {
        id: 'title',
        header: t('chg.opTitle'),
        meta: { exportValue: (r) => r.title },
        cell: ({ row }) => {
          const zZewnatrz = autorZZewnatrz(row.original);
          return (
            <div className="min-w-0">
              <span className="text-sm">{row.original.title}</span>
              {zZewnatrz !== null && (
                <Badge variant="neutral" className="ml-2" title={`${zZewnatrz} — ${t('chg.externalHint')}`}>
                  {t('chg.external')}
                </Badge>
              )}
              {row.original.authorNote && (
                <div className="text-xs text-muted-foreground">{row.original.authorNote}</div>
              )}
            </div>
          );
        },
      },
      {
        id: 'tables',
        header: t('chg.tables'),
        enableSorting: false,
        meta: { exportValue: (r) => r.tables.join(', ') },
        cell: ({ row }) => (
          <div className="space-y-0.5 font-mono text-xs">
            {row.original.tables.map((tb) => (
              <div key={tb}>{tb}</div>
            ))}
          </div>
        ),
      },
      {
        id: 'count',
        header: t('chg.changeCount'),
        enableSorting: false,
        meta: { exportValue: (r) => r.changes.length, align: 'right' },
        cell: ({ row }) => <span className="tabular-nums">{row.original.changes.length}</span>,
      },
      {
        id: 'sourceCol',
        header: t('chg.source'),
        enableSorting: false,
        meta: { exportValue: (r) => r.source },
        cell: ({ row }) =>
          row.original.source === 'AUTHORING' ? (
            <Badge variant="neutral" title={t('chg.sourceHint.AUTHORING')}>
              <FileCog className="size-3" />
              {t('chg.source.AUTHORING')}
            </Badge>
          ) : (
            <span className="text-xs text-muted-foreground">{t('chg.source.MODULE')}</span>
          ),
      },
      {
        id: 'status',
        header: t('chg.statusCol'),
        /**
         * ⚠️ W EKSPORCIE ZOSTAJE SAM STATUS. Znacznik „przejrzana" jest dla patrzącego; dopisany
         * do kolumny statusu w CSV rozwaliłby każdemu filtr po nazwie stanu.
         */
        meta: { exportValue: (r) => r.status },
        cell: ({ row }) => (
          <div className="space-y-1">
            <StatusZmiany status={row.original.status} />
            {/*
              ⚠️⚠️ WIDAĆ W LIŚCIE, KTÓRE PORAŻKI KTOŚ JUŻ PRZEJRZAŁ. Ślad zamknięcia siedział
              wyłącznie w rozwiniętym wierszu, więc pięć czerwonych plakietek wyglądało identycznie
              przed i po kliknięciu „zamknij wszystkie" — i to jest druga połowa zgłoszenia
              „nic się nie dzieje". Dane były w odpowiedzi od początku, nikt ich nie rysował.
            */}
            {row.original.status === 'FAILED' &&
              row.original.zamknietaO !== null &&
              row.original.zamknietaO !== undefined && (
                <Badge variant="neutral" title={t('chg.przejrzanaHint')}>
                  <Check className="size-3" />
                  {t('chg.przejrzana')}
                </Badge>
              )}
          </div>
        ),
      },
      {
        id: 'actions',
        header: t('table.actions'),
        enableSorting: false,
        meta: { align: 'right' },
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="sm"
            aria-label={`${t('chg.detailTitle')} ${row.original.title}`}
            onClick={() => setOpenOp(row.original.id)}
          >
            {t('chg.details')}
          </Button>
        ),
      },
    ],
    [t],
  );

  const fetchAllRows = useCallback(async (): Promise<Operation[]> => {
    if (!groupId) return [];
    const out: Operation[] = [];
    for (let page = 1; ; page += 1) {
      const chunk = await fetchChanges({ ...params, page, pageSize: 200 });
      out.push(...chunk.rows);
      if (out.length >= chunk.total || chunk.rows.length === 0) break;
    }
    return out;
  }, [params, groupId]);

  if (!groupId) return <p className="text-sm text-muted-foreground">{t('islands.none')}</p>;

  const s = summary.data;
  const oldestDays = s?.oldestPending ? waitingDays(s.oldestPending.createdAt) : null;
  /**
   * ⚠️ LICZBA Z PODSUMOWANIA, NIE Z BIEŻĄCEJ STRONY TABELI. Tabela pokazuje 25 wierszy naraz,
   * więc licząc z niej powiedzielibyśmy „zamknij wszystkie (25)" przy 37 porażkach — czyli
   * przycisk kłamałby o tym, co zrobi.
   */
  const nieudanych = s?.statuses.FAILED ?? 0;
  /**
   * ════════════════════════════════════════
   *  ⚠️⚠️ DWIE RÓŻNE LICZBY I TO JEST SEDNO ZGŁOSZENIA Z 04.09.2026: „jak kliknąłem, żeby zamknąć
   *  te problemy, to w dolnym rogu wyskakuje 0 i nic się nie dzieje".
   *
   *  Cały ten ekran liczył porażki z `statuses.FAILED`, czyli z rozkładu statusów — a ten
   *  o skwitowaniu NIE WIE. Zamknięcie zapisywało `zamknietaO` i nie zmieniało ANI liczby w ramce,
   *  ANI napisu na przycisku, ANI koloru paska. Widok przed i po był identyczny, a przy drugim
   *  kliknięciu nie było już czego zamykać — więc działająca czynność wyglądała na zepsutą.
   *
   *  ⚠️ PULPIT TEN WARUNEK MIAŁ OD POCZĄTKU (`overview.service.ts`: `zamknietaO: null`), i to jest
   *  gorsze niż zwykły brak — dwa ekrany jednego portalu odpowiadały inaczej na to samo pytanie.
   *
   *  ⚠️ PEŁNA SUMA ZOSTAJE, nie kasujemy jej: skwitowana porażka nadal SIĘ NIE UDAŁA i te zmiany
   *  nadal nie dojechały na automaty. Zamknięcie zmienia to, czy jest co ROBIĆ — nie to, co się
   *  stało. Dlatego rekord zostaje na ekranie, tylko przestaje świecić żółtym.
   * ════════════════════════════════════════
   */
  const doPrzejrzenia = s?.nieudaneOtwarte ?? 0;
  const przejrzanych = Math.max(0, nieudanych - doPrzejrzenia);
  /**
   * ⚠️⚠️ PASEK NIE MOŻE BYĆ ZIELONY, GDY SĄ NIEUDANE OPERACJE — zgłoszone 04.09.2026:
   * „nie powinno to główne okienko świecić się na zielono skoro są błędy". Kolor liczył się
   * WYŁĄCZNIE z liczby operacji czekających, więc przy pustej kolejce i pięciu porażkach ekran
   * mówił „wszystko w porządku" nad ramką z błędami. To ta sama ślepota, którą naprawialiśmy dziś
   * w kolumnie SPSUI: podsumowanie twierdziło co innego niż to, co pod nim stoi.
   *
   * ⚠️ PIERWSZEŃSTWO MA PORAżKA, nie kolejka: zmiany czekające DOJADĄ same, nieudane NIE.
   */
  const cosDoZrobienia = doPrzejrzenia > 0 || (s?.pending ?? 0) > 0;
  const zamknijHurtem = async () => {
    setZamykanie(true);
    try {
      const w = await zamknijNieudane(groupId);
      /**
       * ⚠️⚠️ ZERO NIE JEST SUKCESEM I NIE MOŻE BYĆ ZIELONE. To na tym dymku przyszło
       * zgłoszenie: „wyskakuje 0 i nic się nie dzieje". Zielony ptaszek z liczbą 0 mówił „zrobione"
       * i jednocześnie nie mówił NIC o tym, dlaczego nic nie zrobiono — a powody są dwa różne:
       * albo wszystko jest już przejrzane, albo nie ma żadnej porażki.
       */
      if (w.zamkniete > 0) toast.success(t('chg.zamknieteWszystkie').replace('{ile}', String(w.zamkniete)));
      else if (w.przejrzanych > 0) toast.toast(t('chg.nicDoZamkniecia').replace('{ile}', String(w.przejrzanych)));
      else toast.toast(t('chg.brakNieudanych'));
      void klient.invalidateQueries({ queryKey: ['changes'] });
      void klient.invalidateQueries({ queryKey: ['overview'] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('common.error'));
    } finally {
      setZamykanie(false);
    }
  };

  const zwin = async () => {
    setZwijanie(true);
    try {
      const w = await zwinOczekujace(groupId, doZwiniecia);
      /** ⚠️ Zero nie jest sukcesem — ta sama zasada co przy skwitowaniu nieudanych. */
      if (w.zwiniete > 0) {
        toast.success(t('chg.collapseDone').replace('{n}', String(w.zwiniete)).replace('{t}', w.tabela));
      } else {
        toast.toast(t('chg.collapseNone').replace('{t}', w.tabela));
      }
      setPytamOZwiniecie(false);
      void klient.invalidateQueries({ queryKey: ['changes'] });
      void klient.invalidateQueries({ queryKey: ['overview'] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('common.error'));
    } finally {
      setZwijanie(false);
    }
  };

  return (
    <div className="space-y-4">
      {/*
        Kolejka, której nikt nie opróżnia, rośnie — i to jest jedyna uczciwa miara stanu w v1.

        ⚠️⚠️ KOLOR MÓWI, CZY JEST CO ROBIĆ (zgłoszone 01.09.2026): „jak nie ma żadnych zmian
        wiszących, to pasek u góry z kolejką zmian też na zielono". Do tej pory był żółty ZAWSZE,
        także przy pustej kolejce — a wtedy ostrzeżenie jest tłem, nie sygnałem.

        ⚠️ ŻÓŁTY, NIE CZERWONY, przy niepustej: zmiany dojeżdżają cyklami, więc czekanie nie jest
        awarią. Czerwony zostaje dla rzeczy, które nie ruszą same — jak wstrzymana kolejka niżej.
      */}
      <div
        className={cn(
          'rounded-lg border p-4 text-sm',
          cosDoZrobienia
            ? 'border-[var(--warning)]/40 bg-[var(--warning)]/10'
            : 'border-[var(--success)]/40 bg-[var(--success)]/10',
        )}
      >
        <p className="flex items-center gap-2 font-medium">
          <ShieldAlert className="size-4" />
          {t('chg.bannerTitle')}
        </p>
        <div className="mt-2 flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
          <span className="tabular-nums">
            {t('chg.pending')}: <strong>{s?.pending ?? 0}</strong>
          </span>
          <span className="tabular-nums">
            {/*
              ⚠️⚠️ ZMIANY W OCZEKUJĄCYCH, NIE CAŁA HISTORIA. Obok „Czeka operacji" liczba z historii
              wyspy czytała się jak zator (IKEA, 23.09.2026: 22 operacje obok 2,2 mln zmian z awarii
              sprzed dwóch dni). Starsza instancja pola nie odda — wtedy nie zgadujemy i piszemy „—".
            */}
            {t('chg.changesPending')}: <strong>{s?.pendingChangeCount ?? '—'}</strong>
          </span>
          {oldestDays !== null && (
            <span className="tabular-nums">
              {t('chg.oldestWaiting')}: <strong>{t('chg.waitingDays').replace('{n}', String(oldestDays))}</strong>
            </span>
          )}
          <span className="tabular-nums text-muted-foreground">
            {t('chg.fromEditor')}: {s?.sources.AUTHORING ?? 0}
          </span>
        </div>
        <p className="mt-2 text-xs">
          {s?.nothingEverAdvanced ? t('chg.bannerNothingAdvanced') : t('chg.bannerBody')}
        </p>


        {/*
          ══════════════════════════════════════════════════════════════════════════════════════
           DLACZEGO KOLEJKA STOI.

           ⚠️⚠️ ZGŁOSZONE Z ŻYWEJ WYSPY (IKEA, 12 maszyn, 01.09.2026): „trochę martwią mnie te
           wszystkie zmiany w toku, wiszą długo". Osiemnaście operacji stało od dwóch godzin ze
           stanem „w kolejce" i niczym więcej — a portal WIEDZIAŁ, dlaczego: wypisywał to sobie
           do logu instancji, do którego nikt nie ma dostępu.

           ⚠️ ZDANIE, NIE IKONKA: powody są trzy i prowadzą do trzech różnych czynności (postaw
           chmurowego mastera / obudź mastera / obudź maszynę). Kolorowa kropka kazałaby zgadywać.
          ══════════════════════════════════════════════════════════════════════════════════════
        */}
        {s?.wstrzymanie != null && (
          <div className="mt-3 rounded border border-destructive/40 bg-destructive/5 p-3">
            <p className="flex items-center gap-2 text-sm font-medium text-destructive">
              <Ban className="size-4" />
              {t('chg.holdTitle').replace('{n}', String(s.wstrzymanie.operacji))}
            </p>
            <p className="mt-1 text-xs leading-snug">{s.wstrzymanie.zdanie}</p>
            {s.wstrzymanie.wezly.length > 0 && (
              <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                {s.wstrzymanie.wezly.join(' · ')}
              </p>
            )}
          </div>
        )}

        {/*
          ══════════════════════════════════════════════════════════════════════════════════════
           ZWINIĘCIE OCZEKUJĄCYCH OPERACJI JEDNEJ TABELI — awaryjne wyjście z zapchanej kolejki.

           ⚠️⚠️ PORTAL CELOWO TEGO NIE MIAŁ, z uzasadnieniem „wyjmowanie operacji czekającej
           byłoby cichym gubieniem zmian". Obaliła to produkcja (21.09.2026): jedno kliknięcie
           założyło 3430 operacji i 2,2 mln zmian, stan magazynowy stanął za lawiną na wiele dni,
           a portal nie miał ŻADNEJ drogi wyjścia. Brak tej czynności nie chronił przed gubieniem
           zmian — zamieniał awarię kolejki w nieodwracalną.

           ⚠️⚠️ DLATEGO WYBÓR TABELY JEST CZĘŚCIĄ CZYNNOŚCI, a nie filtrem wygody: bez niego
           byłby to jeden przycisk kasujący kolejkę wyspy. Zasięg ma być widoczny w chwili
           klikania — i tak samo stoi potem w rejestrze zmian.

           ⚠️ POKAZUJEMY TYLKO PRZY NIEPUSTEJ KOLEJCE. Na pustej to martwy przycisk, który kusi.

           ⚠️⚠️ LISTA Z `pendingTables`, NIE Z `tables` (IKEA, 23.09.2026). `tables` to cała historia
           wyspy — stało tu „Users_VendingMachineAssignment (2093730)" obok „Zmian w oczekujących: 197",
           więc lista nie sumowała się do liczby nad nią. `tables` zostaje dla filtra historii niżej.
          ══════════════════════════════════════════════════════════════════════════════════════
        */}
        {(s?.pending ?? 0) > 0 && (s?.pendingTables?.length ?? 0) > 0 && (
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <label className="grid gap-1 text-xs">
              {t('chg.collapsePick')}
              <Select
                className="w-72"
                value={doZwiniecia}
                onChange={(e) => setDoZwiniecia(e.target.value)}
                disabled={zwijanie}
              >
                <option value="">—</option>
                {s?.pendingTables?.map((x) => (
                  <option key={x.tableName} value={x.tableName}>
                    {x.tableName} ({x.count})
                  </option>
                ))}
              </Select>
            </label>
            <Button
              variant="outline"
              size="sm"
              disabled={doZwiniecia === '' || zwijanie}
              onClick={() => setPytamOZwiniecie(true)}
            >
              {zwijanie ? <Loader2 className="size-4 animate-spin" /> : <Ban className="size-4" />}
              {t('chg.collapse')}
            </Button>
          </div>
        )}
      </div>

      {/*
        ⚠️ POTWIERDZENIE OSOBNYM OKNEM, nie `confirm()`: zdanie musi nieść NAZWĘ TABELI i to, że
        te operacje nie pojadą na automaty. „Na pewno?" bez zasięgu jest pytaniem o nic.
      */}
      {pytamOZwiniecie && (
        <Dialog open onOpenChange={(v) => !v && setPytamOZwiniecie(false)}>
          <DialogContent title={t('chg.collapse')}>
            <p className="text-sm">{t('chg.collapseConfirm').replace('{t}', doZwiniecia)}</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPytamOZwiniecie(false)} disabled={zwijanie}>
                {t('common.cancel')}
              </Button>
              <Button variant="destructive" onClick={() => void zwin()} disabled={zwijanie}>
                {zwijanie ? <Loader2 className="size-4 animate-spin" /> : null}
                {t('chg.collapse')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/*
       * ═══════════════════════════════════════════════════════════════════════════════════
       *  OSOBNA RAMKA, NIE RAMKA W RAMCE.
       *
       *  ⚠️⚠️ ZGŁOSZONE 04.09.2026: „mamy jedno pole w drugim i kolory się nakładają". Blok
       *  nieudanych stał W ŚRODKU paska kolejki, więc żółte tło kładło się na zielone (albo na
       *  żółte) i powstawał trzeci kolor, który nie znaczył nic.
       *
       *  ⚠️ ALE TO NIE JEST TYLKO SPRAWA WYGLĄDU: porażki i kolejka to DWIE RÓŻNE RZECZY. Kolejka
       *  mówi, co jeszcze dojedzie samo; porażki — co NIE dojedzie. Zagnieżdżenie sugerowało, że
       *  jedno jest częścią drugiego, i kazało czytającemu rozdzielać je samemu.
       *
       *  ⚠️ TEN SAM KSZTAŁT CO PASEK KOLEJKI (`rounded-lg`, `p-4`), bo to teraz karta RÓWNORZĘDNA,
       *  nie wtrącenie. Mniejsza ramka pod większą dalej czytałaby się jak jej część.
       * ═══════════════════════════════════════════════════════════════════════════════════
      */}
      {/*
        ═══════════════════════════════════════════════════════════════════════════════════
         NIEUDANE OPERACJE — JEDNYM RUCHEM.

         ⚠️⚠️ ISTNIEJE, BO 37 KLIKNIĘĆ TO NIE JEST INTERFEJS. Zgłoszenie z produkcji PESA mówiło
         o wyspie z 37 nieudanymi operacjami; bez tego przycisku „można je zamknąć" byłoby
         prawdą tylko teoretycznie.

         ⚠️ OSOBNA RAMKA, NIE KOLEJNA LICZBA W PASKU WYŻEJ. Pasek mówi o KOLEJCE, czyli o tym,
         co jeszcze dojedzie; porażki to coś odwrotnego — rzeczy, które NIE dojadą same. Zlanie
         tych dwóch rzeczy w jeden wiersz kazałoby czytającemu je rozdzielać samemu.

         ⚠️ PODPOWIEDŹ MÓWI, CO ZAMKNIĘCIE ZNACZY. Bez tego przycisk czyta się jak „skasuj błąd",
         a on nie kasuje niczego — operacja zostaje nieudana, przestaje tylko liczyć się do stanu
         wyspy. Ta różnica jest całym sensem tego rozwiązania.
        ═══════════════════════════════════════════════════════════════════════════════════
      */}
      {doPrzejrzenia > 0 && (
        <div className="rounded-lg border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-4 text-sm">
          <p className="flex items-center gap-2 font-medium">
            <ShieldAlert className="size-4 text-[var(--warning)]" />
            {t('chg.nieudanePasek').replace('{ile}', String(doPrzejrzenia))}
          </p>
          <p className="mt-1 text-xs leading-snug text-muted-foreground">{t('chg.nieudanePasekOpis')}</p>
          {/* ⚠️ Ile już przejrzano — inaczej „nieudane: 3" nad pięcioma czerwonymi wierszami wygląda na błąd ekranu. */}
          {przejrzanych > 0 && (
            <p className="mt-1 text-xs text-muted-foreground">
              {t('chg.juzPrzejrzane').replace('{ile}', String(przejrzanych))}
            </p>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setStatus('FAILED')} disabled={status === 'FAILED'}>
              {t('chg.pokazNieudane')}
            </Button>
            <Button size="sm" variant="outline" disabled={zamykanie} onClick={() => void zamknijHurtem()}>
              {zamykanie ? <Loader2 className="size-3.5 animate-spin" /> : <Check className="size-3.5" />}
              {t('chg.zamknijWszystkie').replace('{ile}', String(doPrzejrzenia))}
            </Button>
          </div>
        </div>
      )}

      {/*
        ══════════════════════════════
         WSZYSTKO PRZEJRZANE — REKORD ZOSTAJE, ŻÓŁTE GAŚNIE.

         ⚠️⚠️ BEZ TEJ RAMKI ZAMKNIĘCIE WYGLĄDAŁOBY NA SKASOWANIE PROBLEMU. Pięć operacji się
         nie udało i te zmiany NIE DOJECHAŁY na automaty — to zostaje prawdą po skwitowaniu.
         Gdyby cała informacja znikała z ekranu, „zamknij" znaczyłoby „ukryj", a następny człowiek
         nie miałby skąd wiedzieć, że te zmiany trzeba wprowadzić inną drogą.

         ⚠️ BEZ PRZYCISKU ZAMYKANIA: nie ma czego zamykać, a przycisk, który oddaje zero, jest
         dokładnie tym, na co przyszło zgłoszenie.
        ══════════════════════════════
      */}
      {doPrzejrzenia === 0 && nieudanych > 0 && (
        <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm">
          <p className="flex items-center gap-2 font-medium text-muted-foreground">
            <Check className="size-4" />
            {t('chg.nieudaneWszystkiePrzejrzane').replace('{ile}', String(nieudanych))}
          </p>
          <p className="mt-1 text-xs leading-snug text-muted-foreground">{t('chg.nieudaneZostajaOpis')}</p>
          <div className="mt-2">
            <Button size="sm" variant="outline" onClick={() => setStatus('FAILED')} disabled={status === 'FAILED'}>
              {t('chg.pokazNieudane')}
            </Button>
          </div>
        </div>
      )}

      <DataTable<Operation>
        title={t('nav.changes')}
        columns={columns}
        data={data?.rows ?? []}
        total={data?.total ?? 0}
        state={state}
        onStateChange={onStateChange}
        isLoading={isPending}
        isFetching={isFetching}
        onRefresh={() => void refetch()}
        rowKey={(r) => r.id}
        exportFileName={`zmiany-${island?.code ?? 'wyspa'}`}
        fetchAllRows={fetchAllRows}
        reportKey="changes"
        reportGroupId={groupId}
        emptyText={t('chg.empty')}
        aktywneFiltry={[
          status === '' ? null : t('chg.statusCol') + ': ' + t(`chg.status.${status}`),
          source === '' ? null : t('chg.source') + ': ' + t(`chg.source.${source}`),
          tableName === '' ? null : t('chg.table') + ': ' + tableName,
        ].filter((x): x is string => x !== null)}
        onWyczyscFiltry={() => {
          setStatus('');
          setSource('');
          setTableName('');
          onStateChange({ page: 1 });
        }}
        filters={
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="grid gap-1 text-xs">
              {t('chg.statusCol')}
              <Select
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value as '' | ChangeStatus);
                  onStateChange({ page: 1 });
                }}
              >
                <option value="">{t('chg.filterAll')}</option>
                {(['QUEUED', 'SENT', 'APPLIED', 'CONFIRMED', 'FAILED', 'SUPERSEDED'] as const).map((st) => (
                  <option key={st} value={st}>
                    {t(`chg.status.${st}`)}
                    {s && !(st in s.statuses) ? ` — ${t('chg.filterNone')}` : ''}
                  </option>
                ))}
              </Select>
            </label>
            <label className="grid gap-1 text-xs">
              {t('chg.source')}
              <Select
                value={source}
                onChange={(e) => {
                  setSource(e.target.value as '' | 'MODULE' | 'AUTHORING');
                  onStateChange({ page: 1 });
                }}
              >
                <option value="">{t('chg.filterAll')}</option>
                <option value="MODULE">{t('chg.source.MODULE')}</option>
                <option value="AUTHORING">{t('chg.source.AUTHORING')}</option>
              </Select>
            </label>
            <label className="grid gap-1 text-xs">
              {t('chg.table')}
              <Select
                value={tableName}
                onChange={(e) => {
                  setTableName(e.target.value);
                  onStateChange({ page: 1 });
                }}
              >
                <option value="">{t('chg.filterAll')}</option>
                {s?.tables.map((tb) => (
                  <option key={tb.tableName} value={tb.tableName}>
                    {tb.tableName} ({tb.count})
                  </option>
                ))}
              </Select>
            </label>
          </div>
        }
      />

      {openOp && <DetailModal opId={openOp} groupId={groupId} onClose={() => setOpenOp(null)} />}
    </div>
  );
}
